import { useCallback, useRef } from "react";
import { chatWithBot, getChatHistory, ChatResponse } from "../../../http";
import { Message, Tab } from "../types/chatbot.types";
import { parseActionsFromText } from "../utils/chatbot.utils";

export const useChatMessages = (
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>
) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  const sendMessage = useCallback(async (tabId: string, tab: Tab, message: string) => {
    const withUser: Message[] = [...tab.messages, { from: "user", text: message }];
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, messages: withUser, loading: true } : t)));
    try {
      const res = await chatWithBot({ sessionId: tab.sessionId, beneficiaryId: tab.beneficiaryId, message });
      const data: ChatResponse = res.data;
      setTabs((prev) =>
        prev.map((t) =>
          t.id === tabId
            ? { ...t, loading: false, messages: [...withUser, { from: "bot", text: data.message, actions: data.actions, indexScores: data.indexScores }] }
            : t
        )
      );
    } catch {
      setTabs((prev) =>
        prev.map((t) =>
          t.id === tabId
            ? { ...t, loading: false, messages: [...withUser, { from: "bot", text: "Something went wrong. Please try again." }] }
            : t
        )
      );
    }
  }, []); // eslint-disable-line

  const fetchHistory = useCallback(async (tab: Tab, prepend = false, sendStartIfEmpty = false) => {
    setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, loading: true } : t)));
    try {
      const res = await getChatHistory(tab.sessionId, tab.offset, 20);
      const { messages: hist, hasMore: more } = res.data;
      const mapped: Message[] = hist.map((m) => {
        if (m.role === "assistant") {
          const { text, actions } = parseActionsFromText(m.content);
          return { from: "bot" as const, text, actions };
        }
        return { from: "user" as const, text: m.content };
      });

      if (prepend) {
        const body = bodyRef.current;
        const prevScrollHeight = body?.scrollHeight ?? 0;
        setTabs((prev) =>
          prev.map((t) =>
            t.id === tab.id
              ? { ...t, loading: false, hasMore: more, offset: t.offset + hist.length, messages: [...mapped, ...t.messages] }
              : t
          )
        );
        requestAnimationFrame(() => {
          if (body) body.scrollTop = body.scrollHeight - prevScrollHeight;
        });
      } else {
        if (hist.length === 0 && sendStartIfEmpty) {
          setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, loading: false } : t)));
          sendMessage(tab.id, { ...tab, messages: [] }, "Start");
        } else {
          setTabs((prev) =>
            prev.map((t) =>
              t.id === tab.id
                ? { ...t, loading: false, hasMore: more, offset: hist.length, messages: mapped }
                : t
            )
          );
        }
      }
    } catch {
      setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, loading: false } : t)));
    }
  }, []); // eslint-disable-line

  const handleScroll = (activeTab: Tab) => {
    const body = bodyRef.current;
    if (!body || !activeTab || activeTab.loading || !activeTab.hasMore) return;
    if (body.scrollTop === 0) fetchHistory(activeTab, true);
  };

  return { sendMessage, fetchHistory, handleScroll, bottomRef, bodyRef, scrollToBottom };
};
